import { useState } from "react";
import { Box, Typography, Stack, Chip, Button, Tooltip, Alert, CircularProgress } from "@mui/material";
import { useFirm } from "../../contexts/FirmContext";
import { useBinderVerificationSummary } from "../../hooks/useBinderVerificationSummary";
import { BinderVerificationTable } from "../../components/binder-verification/BinderVerificationTable";
import { BinderVerificationDrawer } from "../../components/binder-verification/BinderVerificationDrawer";
import { FirmSelectorCompact } from "../../components/common/FirmSelector";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "issues_found", label: "Issues found" },
  { value: "processing", label: "Processing" },
  { value: "unverified", label: "Not verified" },
  { value: "partially_scanned", label: "Partially scanned" },
  { value: "unverifiable", label: "Unverifiable" },
  { value: "reviewer_complete", label: "Reviewer complete" },
] as const;

export function OpsBinderVerificationPage() {
  const { currentFirmId } = useFirm();
  const [filter, setFilter] = useState<string>("all");
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const { rows, isLoading, isError } = useBinderVerificationSummary(currentFirmId, filter);
  const unverifiedCount = rows.filter((r) => !r.status).length;

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5">Binder verification</Typography>
          <FirmSelectorCompact />
        </Stack>
        <Tooltip title="Bulk verify all binders — coming soon">
          <span>
            <Button variant="contained" disabled>
              Verify all{unverifiedCount > 0 && ` (${unverifiedCount})`}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {!currentFirmId ? (
        <Alert severity="info">
          Select a firm above to view binders.
        </Alert>
      ) : (
        <>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
            {FILTERS.map((f) => (
              <Chip
                key={f.value}
                label={f.label}
                color={filter === f.value ? "primary" : "default"}
                onClick={() => setFilter(f.value)}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>

          {isLoading && (
            <Box p={4} textAlign="center">
              <CircularProgress />
            </Box>
          )}
          {isError && <Alert severity="error">Failed to load binder summary.</Alert>}
          {!isLoading && !isError && (
            <BinderVerificationTable rows={rows} onRowClick={setSelectedJobId} />
          )}

          <BinderVerificationDrawer
            firmId={currentFirmId}
            jobId={selectedJobId}
            onClose={() => setSelectedJobId(null)}
          />
        </>
      )}
    </Box>
  );
}
